<template>
  <!-- after submission -->
  <div v-if="wasSubmitted" class="container">
    <div class="row">
      <div class="col-12 col-lg-6 col-md-9 offset-md-1">
        <h1 class="h2 mt-3">Vielen Dank für Ihre Bewerbung!</h1>
        <p>Wir werden uns in Kürze persönlich bei Ihnen melden.</p>
      </div>
    </div>
  </div>

  <!-- before submission -->
  <div v-else class="container">
    <div class="row">
      <div class="col-12 col-lg-6 col-md-9 offset-md-1">
        <apply-with-c-v
            :postUrl="apiEndpoints.postCV"
            class="btn btn-lg btn-outline-primary mt-2 mb-5 w-100"
            :disabled="submitDisabled"
            @loadedCVData="applyCVData"
            @submittedCVData="workers++"
            @error="error => errorMessage = error"
        >
          <template v-if="workers < 1">
            <i class="fa-solid fa-file"></i> Bewerben mit Lebenslauf
          </template>
          <template v-else>
            <div class="spinner-border spinner-border-sm" role="status">
              <span class="visually-hidden">Processing...</span>
            </div>
          </template>
        </apply-with-c-v>
      </div>
    </div>
    <form
      ref="form"
      :class="{ 'was-validated': wasValidated }"
      action=""
      enctype="multipart/form-data"
      method="post"
      novalidate
      @submit.prevent.stop="submitForm"
    >
      <div class="row">
        <div class="col-12 col-lg-6 col-md-9 offset-md-1">
          <div class="form-floating mb-3">
            <input
              v-model="formFields.first_name"
              class="form-control"
              name="first_name"
              placeholder="Vorname*"
              required=""
              type="text"
            />
            <div class="invalid-feedback">Bitte geben Sie Ihren Vornamen an</div>
            <label for="first_name">Vorname*</label>
          </div>
        </div>
        <div class="col-12 col-lg-6 col-md-9 offset-md-1">
          <div class="form-floating mb-3">
            <input
              v-model="formFields.last_name"
              class="form-control"
              name="last_name"
              placeholder="Nachname*"
              required=""
              type="text"
            />
            <div class="invalid-feedback">Bitte geben Sie Ihren Nachnamen an</div>
            <label for="last_name">Nachname*</label>
          </div>
        </div>
        <div class="col-12 col-lg-6 col-md-9 offset-md-1">
          <div class="form-floating mb-3">
            <input
              v-model="formFields.email"
              class="form-control"
              name="email"
              placeholder="Email*"
              required=""
              type="email"
            />
            <div class="invalid-feedback">Bitte geben Sie Ihre Email an</div>
            <label for="email">Email*</label>
          </div>
        </div>
        <div class="col-12 col-lg-6 col-md-9 offset-md-1">
          <div class="form-floating mb-3">
            <input
              v-model="formFields.phone"
              class="form-control"
              name="phone"
              placeholder="Telefonnummer*"
              required=""
              type="text"
            />
            <div class="invalid-feedback">Bitte geben Sie Ihre Telefonnummer an</div>
            <label for="phone">Telefonnummer*</label>
          </div>
        </div>
        <div v-if="!showAdditional" class="col-12 col-lg-6 col-md-9 offset-md-1">
          <button
            class="btn btn-lg btn-outline-primary w-100 my-3"
            @click.prevent="showAdditional = true"
          >
            mehr erzählen
          </button>
        </div>
      </div>
      <div v-show="showAdditional" class="row">
        <div class="col-12 col-lg-6 col-md-9 offset-md-1">
          <div class="form-floating mb-3">
            <input
              v-model="formFields.street"
              class="form-control"
              name="street"
              placeholder="Straße"
              type="text"
            />
            <label for="street">Straße</label>
          </div>
        </div>

        <div class="col-12 col-lg-6 col-md-9 offset-md-1">
          <div class="row">
            <div class="col-12 col-lg-6">
              <div class="form-floating mb-3">
                <input
                  v-model="formFields.plz"
                  class="form-control"
                  name="plz"
                  placeholder="PLZ"
                  type="text"
                />
                <label for="plz">PLZ</label>
              </div>
            </div>
            <div class="col-12 col-lg-6">
              <div class="form-floating mb-3">
                <input
                  v-model="formFields.city"
                  class="form-control"
                  name="city"
                  placeholder="Ort"
                  type="text"
                />
                <label for="city">Ort</label>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-6 col-md-9 offset-md-1">
          <div class="row">
            <div class="col-12 col-lg-8">
              <div class="form-floating mb-3">
                <select
                  id="floatingSelect"
                  aria-label="Verfügbarkeit"
                  class="form-select"
                  name="available_from"
                >
                  <option selected value="">Auswählen</option>
                  <option value="ab sofort">ab sofort</option>
                  <option value="in 1 bis 2 Monaten">in 1 bis 2 Monaten</option>
                  <option value="in 3 bis 4 Monaten">in 3 bis 4 Monaten</option>
                  <option value="in 5 bis 6 Monaten">in 5 bis 6 Monaten</option>
                  <option value="in 7 Monaten und später">in 7 Monaten und später</option>
                </select>
                <label for="available_from">Verfügbarkeit</label>
              </div>
            </div>
            <div class="col-12 col-lg-4">
              <div class="form-floating mb-3">
                <input
                  class="form-control"
                  name="salary_expectations"
                  placeholder="Gehaltsvorstellung"
                  type="text"
                />
                <label for="salary_expectations">Gehaltsvorstellung</label>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-6 col-md-9 offset-md-1">
          <div class="form-floating">
            <textarea
              class="form-control mb-3"
              name="message"
              placeholder="Nachricht"
              style="height: 10rem"
            ></textarea>
            <label for="">Nachricht</label>
          </div>
        </div>

        <div class="col-12 col-lg-6 col-md-9 offset-md-1">
          <label class="form-label mb-2"
            >Anschreiben, Zeugnisse und Nachweise anhängen</label
          >

          <div id="my-dropzone" class="dropzone">
            <div class="dz-message">
              <div class="p-4 border rounded text-center">
                Dateien hierher ziehen oder<br />
                <button class="btn btn-link m-0 p-0" type="button">
                  klicken um Datei auszuwählen
                </button>
              </div>
            </div>
            <div class="fallback">
              <input
                id="files"
                multiple
                name="file[]"
                style="display: none"
                type="file"
              />Dateien hierher ziehen oder<br />
              <a class="chooseFiles" href="#">klicken um Datei auszuwählen</a>
            </div>
          </div>

          <div id="preview-template" style="display: none">
            <div
              class="dz-preview dz-file-preview border rounded d-flex flex-column p-3 mt-1"
            >
              <div class="dz-progress progress w-100">
                <div class="dz-upload progress-bar" data-dz-uploadprogress></div>
              </div>
              <div class="dz-details w-100 d-flex">
                <a class="dz-success-mark" style="display: none"
                  ><i class="fas fa-check text-success me-2"></i
                ></a>
                <a class="dz-error-mark" style="display: none"
                  ><i class="fas fa-times text-danger me-2"></i
                ></a>
                <span>
                  <span class="dz-error-message text-danger d-block"
                    ><span data-dz-errormessage></span
                  ></span>
                  <span class="dz-filename"><span data-dz-name></span></span>
                </span>
                <a class="text-body ms-auto" data-dz-remove
                  ><i class="fas fa-fw fa-trash"></i
                ></a>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-6 col-md-9 offset-md-1">
          <button
            class="btn btn-lg btn-outline-primary w-100 my-3"
            @click.prevent="showAdditional = false"
          >
            weniger Infos anzeigen
          </button>
        </div>
      </div>
      <div class="row">
        <div class="col-12 col-md-9 offset-md-1 py-3">
          <div class="bg-body rounded p-5">
            <h2 class="text-center">
              Nutzen Sie die Chance auf den Job der zu Ihnen passt!
            </h2>
            <hr class="my-4" />
            <div class="form-check mb-3">
              <input
                id="approval-radio-01"
                class="form-check-input custom-control custom-radio"
                name="privacy"
                required
                type="checkbox"
                value="ja"
              />
              <label class="form-check-label" for="approval-radio-01">
                <p class="mb-2">
                  Ich bin damit einverstanden, dass Helmi Göttler GmbH meine Bewerbung für die Dauer von 24 Monaten speichert. Eine Übermittlung relevanter Bewerbungsdaten an Kunden und Interessenten kann zur möglichen Besetzung vakanter Stellen bei Kunden der Helmi Göttler GmbH in diesem Zeitraum stattfinden, ohne dass ich mich aktiv auf diese Stellenangebote beworben habe. Sollten auf diese Weise geeignet erscheinende Stellen identifiziert werden, werde ich eine entsprechende Verständigung (per Mail, telefonisch oder per mobiler Textnachricht) erhalten, womit ich einverstanden bin. Ich kann meine Einwilligung jederzeit widerrufen.
                </p>
              </label>
            </div>
            <p>
              Unsere Datenschutzerklärung finden Sie unter
              <a :href="privacyUrl">Datenschutzhinweise</a>.
            </p>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12 col-lg-6 col-md-9 offset-md-1 py-3">
          <input id="art" :value="requestData['jobBoard']" name="art" type="hidden" />
          <button
            :disabled="submitDisabled"
            class="btn btn-lg btn-primary text-white w-100"
            type="submit"
          >
            <template v-if="workers > 0">
              <div class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Processing...</span>
              </div>
            </template>
            <template v-else> Formular absenden </template>
          </button>
        </div>
      </div>
    </form>
  </div>
  <toaster v-model="errorMessage"></toaster>
</template>

<script setup>
import Dropzone from "dropzone";
import { computed, onMounted, reactive, ref } from "vue";
import ApplyWithCV from "./ApplyWithCV";
import Toaster from "../Toaster";

// props
const props = defineProps({
  apiEndpoints: Object,
  requestData: Object,
  privacyUrl: String,
});

// reactive data
const showAdditional = ref(false);

const uploadingFile = ref(false);
const wasValidated = ref(false);
const wasSubmitted = ref(false);

const uploadProgress = ref(0);
const workers = ref(0);

const form = ref("");

const formFields = reactive({
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  street: "",
  plz: "",
  city: "",
});

const errorMessage = ref("");

// computed
const submitDisabled = computed(() => {
  return uploadingFile.value || workers.value > 0;
});

// global definitions
let dzClosure;

// get csrfToken
const csrfToken = () => document.head.querySelector('meta[name="csrf-token"]').content;

// apply data from CV to form fields
const applyCVData = (data, file) => {
  // apply data
  for (let property in data) {
    if (formFields.hasOwnProperty(property) && data[property] !== "") {
      formFields[property] = data[property];
    }
  }
  // expand additional and append file to dropzone
  showAdditional.value = true;
  setTimeout(() => {
    dzClosure.addFile(file);
    workers.value--;
  }, 500);
};

// submit form
const submitForm = () => {
  // if passes validation
  if (form.value.checkValidity()) {
    workers.value++;
    // post form data
    const formData = new FormData(form.value);
    axios
      .post("/ajax/" + props.apiEndpoints.application, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-CSRF-TOKEN": csrfToken(),
        },
        onUploadProgress: (progressEvent) => {
          uploadProgress.value =
            Math.round(progressEvent.loaded / progressEvent.total) * 100;
        },
      })
      .then((response) => {
        wasSubmitted.value = true;
      })
      .catch((error) => {
        errorMessage.value = "Ein Fehler ist aufgetreten, versuche es später erneut.";
      })
      .finally(() => {
        uploadProgress.value = 0;
        workers.value--;
      });
  }
  wasValidated.value = true;
};

// lifecycle hooks
onMounted(() => {
  new Dropzone("#my-dropzone", {
    url: "/ajax/" + props.apiEndpoints.fileUpload,
    previewTemplate:
      document.querySelector("#preview-template") !== null
        ? document.querySelector("#preview-template").innerHTML
        : "",
    createImageThumbnails: true,
    uploadMultiple: true,
    parallelUploads: 2,
    maxFiles: 10,
    timeout: 0,
    maxFilesize: 10, //MB
    acceptedFiles: ".pdf,.doc,.docx,.jpg,.jpeg,.png",
    addRemoveLinks: true,
    headers: {
      "X-CSRF-TOKEN": csrfToken(),
    },
    dictRemoveFile: "",
    dictInvalidFileType: "Dateityp nicht erlaubt, nur PDF, JPG, JPEG, PNG, DOC und DOCX",
    dictFileTooBig: "Datei zu Groß ({{filesize}}MB), Maximalgröße {{maxFilesize}}MB",
    dictResponseError: "Server antwortet mit Code {{statusCode}}",
    dictCancelUploadConfirmation: "Upload wirklich abbrechen?",
    dictMaxFilesExceeded: "Keine weiteren Dateien möglich.",
    dictCancelUpload: "<i class='fas fa-fw fa-times ml-3 text-danger'></i>",
    success: function (file, response) {
      file.previewElement.querySelector(".dz-progress").remove();
    },
    // ST BUG FIX:  success and error marks are displayed regardless of the actual upload result
    init: function () {
      dzClosure = this; // Makes sure that 'this' is understood inside the functions below.
      this.on("removedfile", function (file) {
        axios.post(
          "/ajax/" + props.apiEndpoints.fileDelete,
          { filename: file.name },
          {
            headers: {
              "X-CSRF-TOKEN": csrfToken(),
            },
          }
        );
      });

      this.on("addedfile", function () {
        uploadingFile.value = true;
      });

      this.on("queuecomplete", function () {
        uploadingFile.value = false;
      });

      this.on("sending", function (file) {
        // On starting the upload, hide both marks
        file.previewElement.querySelector(".dz-success-mark").style.display = "none";
        file.previewElement.querySelector(".dz-error-mark").style.display = "none";
      });

      this.on("success", function (file) {
        // On successful upload, show only the success mark
        file.previewElement.querySelector(".dz-success-mark").style.display = "block";
      });

      this.on("error", function (file) {
        // On upload error, show only the error mark
        file.previewElement.querySelector(".dz-error-mark").style.display = "block";
      });
      this.on("errormultiple", function (file, response) {});
    },
  });
});
</script>

<style scoped></style>
